import React from "react";
import { StyleSheet, View, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import {
  MediaModel,
  IMediaMetadata,
  formatLocation,
  DateParser,
  isDateUserDefined,
  formatCardTimestamp,
} from "common247";
import { ThemedText } from "../ThemedText";
import { hasExtractedMetadata } from "@/services/media-metadata";
import {
  formatFileSize,
  formatContentType,
  formatDuration,
  formatDimensions,
  formatCameraSettings,
  formatShortHash,
} from "./media-formatters";

/**
 * Formats a media creation timestamp into canonical card timestamp (dd-MMM-yyyy HH:mm:ss).
 * @param {number} [timestamp] - Unix epoch milliseconds timestamp.
 * @returns {string} Human-readable date and time representation.
 */
export function formatUploadedDateTime(timestamp?: number): string {
  if (!timestamp) return "Unknown date";
  return formatCardTimestamp(timestamp) || "Unknown date";
}

/**
 * Displays offline-extracted media metadata: captured date, GPS location, camera settings, dimensions, objects found, and description.
 */
export function MediaMetadataSection({
  metadata,
  mediaTarget,
  badgeBg,
  tintColor,
}: {
  metadata: IMediaMetadata;
  mediaTarget?: string;
  badgeBg: string;
  tintColor: string;
}) {
  if (!hasExtractedMetadata(metadata) && (!mediaTarget || mediaTarget === "Unknown")) return null;

  const capturedLabel = metadata.capturedAt
    ? new Date(metadata.capturedAt).toLocaleString()
    : null;

  const validGps =
    typeof metadata.gpsLatitude === "number" &&
    !isNaN(metadata.gpsLatitude) &&
    typeof metadata.gpsLongitude === "number" &&
    !isNaN(metadata.gpsLongitude);
  const locationLabel =
    metadata.gpsLocationLabel ||
    (validGps ? `${metadata.gpsLatitude!.toFixed(4)}, ${metadata.gpsLongitude!.toFixed(4)}` : null);

  const cameraSettings = formatCameraSettings(metadata.extraMetadata);
  const dimLabel = formatDimensions(metadata.width, metadata.height);
  const durationLabel = formatDuration(metadata.durationMs);

  const objectsFound = metadata.objects ?? [];
  const tags = metadata.tags ?? [];
  const chips = [...objectsFound, ...tags].slice(0, 10);

  return (
    <View style={metaStyles.container}>
      <ThemedText type="stamp" style={metaStyles.sectionLabel}>
        Archival Record
      </ThemedText>

      {mediaTarget && mediaTarget !== "Unknown" ? (
        <View style={metaStyles.row}>
          <Ionicons name="flag-outline" size={13} color={tintColor} />
          <ThemedText style={metaStyles.rowText}>Target: {mediaTarget}</ThemedText>
        </View>
      ) : null}

      {metadata.href ? (
        <View style={metaStyles.row}>
          <Ionicons name="link-outline" size={13} color={tintColor} />
          <ThemedText style={metaStyles.rowText} numberOfLines={1} selectable>
            HRef: {metadata.href}
          </ThemedText>
        </View>
      ) : null}

      {capturedLabel ? (
        <View style={metaStyles.row}>
          <Ionicons name="calendar-outline" size={13} color={tintColor} />
          <ThemedText style={metaStyles.rowText}>Captured: {capturedLabel}</ThemedText>
        </View>
      ) : null}

      {cameraSettings ? (
        <View style={metaStyles.row}>
          <Ionicons name="camera-outline" size={13} color={tintColor} />
          <ThemedText style={metaStyles.rowText} numberOfLines={1}>
            Camera: {cameraSettings}
          </ThemedText>
        </View>
      ) : null}

      {dimLabel ? (
        <View style={metaStyles.row}>
          <Ionicons name="scan-outline" size={13} color={tintColor} />
          <ThemedText style={metaStyles.rowText}>Dimensions: {dimLabel} px</ThemedText>
        </View>
      ) : durationLabel ? (
        <View style={metaStyles.row}>
          <Ionicons name="time-outline" size={13} color={tintColor} />
          <ThemedText style={metaStyles.rowText}>Duration: {durationLabel}</ThemedText>
        </View>
      ) : null}

      {locationLabel ? (
        <View style={metaStyles.row}>
          <Ionicons name="location-outline" size={13} color={tintColor} />
          <ThemedText style={metaStyles.rowText}>Location: {locationLabel}</ThemedText>
        </View>
      ) : null}

      {metadata.description ? (
        <View style={metaStyles.descRow}>
          <Ionicons name="document-text-outline" size={13} color={tintColor} style={metaStyles.descIcon} />
          <ThemedText style={metaStyles.descText}>{metadata.description}</ThemedText>
        </View>
      ) : null}

      {chips.length > 0 ? (
        <View style={metaStyles.objectsContainer}>
          <ThemedText style={metaStyles.objectsLabel}>Objects found:</ThemedText>
          <View style={metaStyles.chipsRow}>
            {chips.map((chip, idx) => (
              <View key={`${chip}-${idx}`} style={[metaStyles.chip, { backgroundColor: badgeBg }]}>
                <ThemedText style={[metaStyles.chipText, { color: tintColor }]}>{chip}</ThemedText>
              </View>
            ))}
          </View>
        </View>
      ) : null}
    </View>
  );
}

/**
 * Props for the MediaStatsSection component.
 */
export interface IMediaStatsSectionProps {
  media: MediaModel;
  badgeBg: string;
  borderColor: string;
  tintColor: string;
  compact?: boolean;
}

/**
 * Displays media file specifications and statistics:
 * file size, MIME format, dimensions/duration, SHA-256 integrity checksum,
 * and memory/sharing linkage counts.
 */
export function MediaStatsSection({
  media,
  badgeBg,
  borderColor,
  tintColor,
}: IMediaStatsSectionProps) {
  const fileSize = formatFileSize(media.fileSizeBytes);
  const format = formatContentType(media.contentType);
  const dimensions = formatDimensions(media.metadata?.width, media.metadata?.height);
  const duration = formatDuration(media.metadata?.durationMs);
  const checksum = formatShortHash(media.fileHash);
  const memoryCount = media.memoryItems?.length ?? media.memoryIds?.length ?? 0;
  const sharedCount = (media.sharedTo?.length ?? 0) > 0 ? media.sharedTo!.length : (media.sharedWith?.length ?? 0);

  return (
    <View style={[statsStyles.container, { borderColor }]}>
      <View style={statsStyles.headerRow}>
        <ThemedText type="stamp" style={statsStyles.sectionLabel}>
          Specifications & Stats
        </ThemedText>
        {checksum ? (
          <ThemedText style={[statsStyles.hashTag, { color: tintColor }]}>
            {checksum}
          </ThemedText>
        ) : null}
      </View>

      <View style={statsStyles.grid}>
        {/* File Size */}
        <View style={[statsStyles.statBox, { backgroundColor: badgeBg }]}>
          <Ionicons name="save-outline" size={12} color={tintColor} />
          <View style={statsStyles.statTextContainer}>
            <ThemedText style={statsStyles.statLabel}>Size</ThemedText>
            <ThemedText type="defaultSemiBold" style={statsStyles.statValue}>
              {fileSize}
            </ThemedText>
          </View>
        </View>

        {/* Format */}
        <View style={[statsStyles.statBox, { backgroundColor: badgeBg }]}>
          <Ionicons name="document-text-outline" size={12} color={tintColor} />
          <View style={statsStyles.statTextContainer}>
            <ThemedText style={statsStyles.statLabel}>Format</ThemedText>
            <ThemedText type="defaultSemiBold" style={statsStyles.statValue}>
              {format}
            </ThemedText>
          </View>
        </View>

        {/* Dimensions or Duration */}
        {dimensions ? (
          <View style={[statsStyles.statBox, { backgroundColor: badgeBg }]}>
            <Ionicons name="scan-outline" size={12} color={tintColor} />
            <View style={statsStyles.statTextContainer}>
              <ThemedText style={statsStyles.statLabel}>Resolution</ThemedText>
              <ThemedText type="defaultSemiBold" style={statsStyles.statValue}>
                {dimensions}
              </ThemedText>
            </View>
          </View>
        ) : duration ? (
          <View style={[statsStyles.statBox, { backgroundColor: badgeBg }]}>
            <Ionicons name="time-outline" size={12} color={tintColor} />
            <View style={statsStyles.statTextContainer}>
              <ThemedText style={statsStyles.statLabel}>Duration</ThemedText>
              <ThemedText type="defaultSemiBold" style={statsStyles.statValue}>
                {duration}
              </ThemedText>
            </View>
          </View>
        ) : null}

        {/* Memories Linkage */}
        <View style={[statsStyles.statBox, { backgroundColor: badgeBg }]}>
          <Ionicons name="book-outline" size={12} color={tintColor} />
          <View style={statsStyles.statTextContainer}>
            <ThemedText style={statsStyles.statLabel}>Memories</ThemedText>
            <ThemedText type="defaultSemiBold" style={statsStyles.statValue}>
              {memoryCount > 0 ? `${memoryCount} linked` : "None linked"}
            </ThemedText>
          </View>
        </View>

        {/* Sharing Reach */}
        <View style={[statsStyles.statBox, { backgroundColor: badgeBg }]}>
          <Ionicons name={sharedCount > 0 ? "people-outline" : "lock-closed-outline"} size={12} color={tintColor} />
          <View style={statsStyles.statTextContainer}>
            <ThemedText style={statsStyles.statLabel}>Sharing</ThemedText>
            <ThemedText type="defaultSemiBold" style={statsStyles.statValue}>
              {sharedCount > 0 ? `${sharedCount} recipient${sharedCount === 1 ? "" : "s"}` : "Private"}
            </ThemedText>
          </View>
        </View>
      </View>
    </View>
  );
}

/**
 * Props for the MediaHoverMetadataFooter component.
 */
export interface IMediaHoverMetadataFooterProps {
  media: MediaModel;
  borderColor: string;
  badgeBg: string;
  tintColor: string;
}

/**
 * Compact metadata footer displayed in very small font size at the bottom of the media card on hover.
 * Displays technical specs, captured/uploaded dates, camera parameters, GPS location, and AI tags.
 */
export function MediaHoverMetadataFooter({
  media,
  borderColor,
  badgeBg,
  tintColor,
}: IMediaHoverMetadataFooterProps) {
  const metadata = media.metadata;
  const dimLabel = formatDimensions(metadata?.width, metadata?.height);
  const durationLabel = formatDuration(metadata?.durationMs);
  const fileSizeLabel = formatFileSize(media.fileSizeBytes);
  const typeLabel = formatContentType(media.contentType);
  const hashLabel = formatShortHash(media.fileHash);

  const capturedLabel = metadata?.capturedAt ? formatUploadedDateTime(metadata.capturedAt) : null;
  const uploadedLabel = formatUploadedDateTime(media.createdAt);

  const cameraSettings = formatCameraSettings(
    metadata?.extraMetadata ||
      ((metadata as any)?.make || (metadata as any)?.model
        ? {
            make: (metadata as any).make,
            model: (metadata as any).model,
            fNumber: (metadata as any).fNumber,
            exposureTime: (metadata as any).exposureTime,
            iso: (metadata as any).iso,
            focalLength: (metadata as any).focalLength,
          }
        : undefined)
  );

  const validGps =
    typeof metadata?.gpsLatitude === "number" &&
    !isNaN(metadata.gpsLatitude) &&
    typeof metadata?.gpsLongitude === "number" &&
    !isNaN(metadata.gpsLongitude);
  const locFormatted = formatLocation(media.location);
  const locationLabel =
    locFormatted ||
    metadata?.gpsLocationLabel ||
    (validGps ? `${metadata!.gpsLatitude!.toFixed(4)}, ${metadata!.gpsLongitude!.toFixed(4)}` : null);

  const objectsFound = [...(metadata?.objects ?? []), ...(metadata?.tags ?? [])];
  const tagsString = objectsFound.slice(0, 5).join(", ");
  const description = metadata?.description;

  return (
    <View
      style={[hoverMetaStyles.footerContainer, { borderTopColor: borderColor, backgroundColor: badgeBg }]}
      testID={`media-hover-metadata-${media.id || "card"}`}
    >
      <View style={hoverMetaStyles.row}>
        {dimLabel ? (
          <ThemedText style={hoverMetaStyles.text} numberOfLines={1}>
            📐 {dimLabel}
          </ThemedText>
        ) : durationLabel ? (
          <ThemedText style={hoverMetaStyles.text} numberOfLines={1}>
            ⏱️ {durationLabel}
          </ThemedText>
        ) : null}

        <ThemedText style={hoverMetaStyles.text} numberOfLines={1}>
          💾 {fileSizeLabel}
        </ThemedText>

        <ThemedText style={[hoverMetaStyles.text, { color: tintColor, fontWeight: "700" }]} numberOfLines={1}>
          {typeLabel}
        </ThemedText>

        {hashLabel ? (
          <ThemedText style={hoverMetaStyles.textMuted} numberOfLines={1}>
            🔒 {hashLabel}
          </ThemedText>
        ) : null}
      </View>

      <View style={hoverMetaStyles.row}>
        {media.date ? (
          <ThemedText style={[hoverMetaStyles.text, { color: tintColor, fontWeight: "700" }]} numberOfLines={1}>
            📅 {DateParser.parse(media.date).formatted} {isDateUserDefined(media.date) ? "(Manual)" : ""}
          </ThemedText>
        ) : null}

        <ThemedText style={hoverMetaStyles.text} numberOfLines={1}>
          {capturedLabel ? `🕒 ${capturedLabel}` : `📤 ${uploadedLabel}`}
        </ThemedText>

        {cameraSettings ? (
          <ThemedText style={hoverMetaStyles.text} numberOfLines={1}>
            📷 {cameraSettings}
          </ThemedText>
        ) : null}

        {locationLabel ? (
          <ThemedText style={hoverMetaStyles.text} numberOfLines={1}>
            📍 {locationLabel}
          </ThemedText>
        ) : null}
      </View>

      {description ? (
        <ThemedText style={hoverMetaStyles.textItalic} numberOfLines={1}>
          📝 {description}
        </ThemedText>
      ) : tagsString ? (
        <ThemedText style={hoverMetaStyles.textMuted} numberOfLines={1}>
          🏷️ {tagsString}
        </ThemedText>
      ) : null}
    </View>
  );
}

const statsStyles = StyleSheet.create({
  container: {
    marginTop: 6,
    padding: 6,
    borderRadius: 0,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    gap: 4,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2,
  },
  sectionLabel: {
    fontSize: 8.5,
    fontWeight: "bold",
    opacity: 0.5,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  hashTag: {
    fontSize: 8.5,
    fontFamily: Platform.OS === "ios" ? "Courier" : "monospace",
    fontWeight: "600",
    opacity: 0.8,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
  statBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 0,
    flexGrow: 1,
    minWidth: 85,
  },
  statTextContainer: {
    justifyContent: "center",
  },
  statLabel: {
    fontSize: 7.5,
    fontWeight: "bold",
    textTransform: "uppercase",
    opacity: 0.45,
    lineHeight: 9,
  },
  statValue: {
    fontSize: 10,
    lineHeight: 12,
  },
});

const metaStyles = StyleSheet.create({
  container: {
    marginTop: 4,
    padding: 4,
    borderRadius: 0,
    backgroundColor: "rgba(255, 91, 3, 0.06)",
    borderWidth: 1,
    borderColor: "rgba(255, 91, 3, 0.2)",
    gap: 3,
  },
  sectionLabel: {
    fontSize: 8.5,
    fontWeight: "bold",
    opacity: 0.45,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 1,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  rowText: {
    fontSize: 9.5,
    opacity: 0.75,
  },
  descRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 3,
    marginTop: 1,
  },
  descIcon: {
    marginTop: 1,
  },
  descText: {
    fontSize: 9.5,
    fontStyle: "italic",
    opacity: 0.85,
    flex: 1,
    lineHeight: 13,
  },
  objectsContainer: {
    marginTop: 1,
    gap: 2,
  },
  objectsLabel: {
    fontSize: 9,
    fontWeight: "600",
    opacity: 0.7,
  },
  chipsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2,
  },
  chip: {
    paddingHorizontal: 4,
    paddingVertical: 1,
    borderRadius: 0,
  },
  chipText: {
    fontSize: 8.5,
    fontWeight: "600",
  },
});

const hoverMetaStyles = StyleSheet.create({
  footerContainer: {
    borderTopWidth: 1,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 0,
    gap: 1.5,
  },
  row: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 4,
  },
  text: {
    fontSize: 8.5,
    lineHeight: 11,
    opacity: 0.85,
  },
  textMuted: {
    fontSize: 8,
    lineHeight: 10,
    opacity: 0.65,
  },
  textItalic: {
    fontSize: 8.5,
    lineHeight: 11,
    fontStyle: "italic",
    opacity: 0.8,
  },
});
